import React, { useState } from 'react';
import {
  Form,
  Input,
  Row,
  Button,
  Divider,
  Select,
  InputNumber,
  Col,
  Alert
} from 'antd';
import { MaskedInput } from 'antd-mask-input';
import useSegmentField from 'core/hooks/useSegmentField';
import {
  formartOnlyNumber,
  configManager,
  FormItemWrap
} from '@unicus/react-ui-kit';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import Iugu from 'util/iugu';
import ResCol from 'components/forms/ResCol';
import { formatCurrency } from 'util/formatters';
import { BsCheck2Square } from 'react-icons/bs';
import { useSubscriptionPlanHelper } from 'core/hooks';
import usePermissions from 'core/hooks/usePermissions';
import CreditCardIcon from './CreditCardIcon';

const PaymentPlan = ({ loading, onSubmit, plan, onCancel }) => {
  const segmentFields = useSegmentField();
  const { search } = useLocation();
  const { applypartnerdiscount } = qs.parse(search);
  const [validatingCard, setValidatingCard] = useState(false);
  const { isPartnership } = usePermissions();
  const { getFeatureValue, identifiers } = useSubscriptionPlanHelper(plan);

  const min = isPartnership
    ? getFeatureValue(identifiers.MIN_LICENSE)
    : getFeatureValue(identifiers.MIN_PROFESSIONAL_NUMBER);

  const max = isPartnership
    ? getFeatureValue(identifiers.MAX_LICENSE)
    : getFeatureValue(identifiers.MAX_PROFESSIONAL_NUMBER);

  const amountInitial =
    (isPartnership
      ? getFeatureValue(identifiers.MIN_LICENSE)
      : getFeatureValue(identifiers.MIN_PROFESSIONAL_NUMBER)) * plan.amount;

  const [amount, setAmount] = useState(amountInitial);
  const [form] = Form.useForm();

  const validateCreditCard = ({
    number,
    expirationDate,
    cardholderName,
    securityCode,
    installments = null,
    numberOfPaidProfessionals,
    numberOfLicenses
  }) => {
    const month = expirationDate.substr(0, 2);
    const year = expirationDate.substr(3, 6);
    const [firstName, ...lastNames] = cardholderName.split(' ');
    const lastName = lastNames.join(' ');

    const cc = Iugu.CreditCard(
      number,
      month,
      year,
      firstName,
      lastName,
      securityCode
    );

    if (cc.valid()) {
      setValidatingCard(true);
      Iugu.createPaymentToken(cc, (response) => {
        setValidatingCard(false);

        onSubmit({
          numberOfPaidProfessionals,
          numberOfLicenses,
          amount,
          removeLicense: !applypartnerdiscount,
          paymentMethod: {
            creditCard: {
              token: response.id,
              holder: {
                name: cardholderName
              }
            },
            installments
          }
        });
      });
    }
  };

  const handleFinish = (values) => {
    validateCreditCard(values);
  };

  const validateNumber = (rule, value) => {
    if (!value) {
      return Promise.resolve();
    }

    const number = formartOnlyNumber(value);

    if (number.length <= 16 && !Iugu.utils.validateCreditCardNumber(number)) {
      return Promise.reject(new Error('Número de cartão inválido'));
    }

    return Promise.resolve();
  };

  const validateExpirationDate = (rule, value) => {
    if (!value) {
      return Promise.resolve();
    }

    const date = formartOnlyNumber(value);

    if (date.length === 6 && !Iugu.utils.validateExpirationString(value)) {
      return Promise.reject(new Error('Data de validade inválida'));
    }

    return Promise.resolve();
  };

  const validateName = (rule, value) => {
    if (!value) {
      return Promise.resolve();
    }

    if (/\d/.test(value)) {
      return Promise.reject(new Error('Nome não pode conter números'));
    }

    if (value.split(' ').filter((c) => c !== '').length === 1) {
      return Promise.reject(new Error('Digite o nome completo'));
    }

    return Promise.resolve();
  };

  const validateSecurityCode = (value, number) => {
    if (!value || !number) {
      return true;
    }

    const cardNumber = formartOnlyNumber(number);
    const cvv = formartOnlyNumber(value);
    const brand = Iugu.utils.getBrandByCreditCardNumber(cardNumber);

    if (
      cardNumber.length === 16 &&
      cvv.length === 3 &&
      !Iugu.utils.validateCVV(cvv, brand)
    ) {
      return false;
    }

    return true;
  };

  const validateNumberOfProfessionals = (rule, value) => {
    if (!value && value !== 0) {
      return Promise.resolve();
    }

    const label = isPartnership ? 'licenças' : 'profissionais';

    if (!Number.isNaN(Number(min)) && value < Number.parseInt(min, 10)) {
      return Promise.reject(
        new Error(`O plano tem um limite mínimo de ${min} ${label}`)
      );
    }

    if (!Number.isNaN(Number(max)) && value > Number.parseInt(max, 10)) {
      return Promise.reject(
        new Error(`O plano tem um limite máximo de ${max} ${label}`)
      );
    }

    return Promise.resolve();
  };

  const handleChangeValues = (
    _,
    { numberOfPaidProfessionals, numberOfLicenses }
  ) => {
    if (!isPartnership) {
      setAmount(numberOfPaidProfessionals * plan.amount);
      return;
    }

    if (isPartnership) {
      setAmount(numberOfLicenses * plan.amount);
    }
  };

  const installmentsData = [];

  for (let parcel = 1; parcel < 13; parcel += 1) {
    installmentsData.push(parcel);
  }

  const isLoading = loading || validatingCard;

  return (
    <Form
      {...configManager.getConfig().layout.form}
      name="payments"
      className="gx-h-100"
      form={form}
      initialValues={{
        installments: 1,
        numberOfPaidProfessionals: isPartnership ? 0 : 1,
        numberOfLicenses: isPartnership ? 1 : 0
      }}
      onFinish={handleFinish}
      validateTrigger="onBlur"
      onValuesChange={handleChangeValues}
    >
      <Row>
        <ResCol span={12}>
          <Divider orientation="left">Resumo da assinatura</Divider>
          <div className="gx-card-plan-list">
            <div>
              <ul>
                <div className="gx-fs-lg gx-font-weight-semi-bold gx-mb-3">
                  {plan.name}
                </div>
                {plan.features.map((feature, index) => (
                  <li key={feature.id}>
                    <React.Fragment key={feature.description}>
                      <BsCheck2Square />
                      <span>{feature.description}</span>
                    </React.Fragment>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <Row
            className="gx-mb-3"
            align="middle"
            justify="start"
            style={{ width: '300px' }}
          >
            <Col flex="150px">
              <span className="gx-font-weight-bold gx-pb-3">
                {!isPartnership
                  ? `Quant. ${segmentFields.serviceProvider}`
                  : 'Quant. Licença'}
              </span>
            </Col>
            <Col>
              <FormItemWrap
                name={
                  !isPartnership
                    ? 'numberOfPaidProfessionals'
                    : 'numberOfLicenses'
                }
                required
                noStyle
                rules={[
                  () => ({
                    validator: validateNumberOfProfessionals
                  })
                ]}
                autoFocus
              >
                <InputNumber
                  style={{ width: '120px' }}
                  min={!Number.isNaN(Number(min)) ? Number(min) : undefined}
                  max={!Number.isNaN(Number(max)) ? Number(max) : undefined}
                />
              </FormItemWrap>
            </Col>
          </Row>

          <Row align="middle" style={{ width: '290px' }}>
            <Col span={24}>
              <Alert
                message={
                  <div className="gx-fs-lg">
                    <span className="gx-mr-1">
                      {plan.frequency === 'yearly'
                        ? `Assinatura anual`
                        : `Assinatura mensal`}
                    </span>
                    <span className="gx-font-weight-bold">
                      {formatCurrency(amount)}
                    </span>
                  </div>
                }
                type="info"
              />
            </Col>
          </Row>
        </ResCol>
        <ResCol span={12}>
          <Row>
            <ResCol>
              <FormItemWrap
                label="Nome do titular"
                name="cardholderName"
                whitespace
                required
                rules={[
                  () => ({
                    validator: validateName
                  })
                ]}
              >
                <Input />
              </FormItemWrap>
            </ResCol>
          </Row>
          <Row>
            <ResCol>
              <FormItemWrap
                label="Número do Cartão"
                name="number"
                rules={[
                  { required: true, message: 'Informe o número do cartão' },
                  () => ({
                    validator: validateNumber
                  })
                ]}
              >
                <MaskedInput
                  mask="1111 1111 1111 1111"
                  suffix={
                    <Form.Item name="number" valuePropName="number" noStyle>
                      <CreditCardIcon />
                    </Form.Item>
                  }
                />
              </FormItemWrap>
            </ResCol>
          </Row>
          <Row>
            <ResCol span={12}>
              <FormItemWrap
                label="Data de Validade"
                name="expirationDate"
                required
                rules={[
                  () => ({
                    validator: validateExpirationDate
                  })
                ]}
              >
                <MaskedInput mask="11/1111" />
              </FormItemWrap>
            </ResCol>
            <ResCol span={12}>
              <FormItemWrap
                label="Código de Segurança"
                name="securityCode"
                required
                rules={[
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (
                        !validateSecurityCode(value, getFieldValue('number'))
                      ) {
                        return Promise.reject(
                          new Error('Código de segurança inválido')
                        );
                      }

                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <MaskedInput mask="111" />
              </FormItemWrap>
            </ResCol>
          </Row>
          {plan.frequency === 'yearly' ? (
            <FormItemWrap label="Parcelamento" name="installments" required>
              <Select
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.props.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {installmentsData.map((c) => (
                  <Select.Option key={c.toString()} value={c}>
                    {`${c}x ${formatCurrency(plan.amount / c)}`}
                  </Select.Option>
                ))}
              </Select>
            </FormItemWrap>
          ) : null}
        </ResCol>
      </Row>
      <Row justify="end" gutter={[5, 0]} className="gx-mt-5">
        <Col>
          <Button onClick={onCancel} disabled={isLoading}>
            Voltar
          </Button>
        </Col>
        <Col flex="222px">
          <Button
            type="primary"
            onClick={form.submit}
            loading={isLoading}
            disabled={isLoading}
          >
            Confirmar pagamento
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default PaymentPlan;

